import { startOfToday } from "date-fns";
import { date, mixed, object, string } from "yup";

import { unit } from "@covertree/utils";
import type { StateCode } from "@covertree/consts";
import {
  getAgentHouseDetailsFormSchema,
  personalDetailsFormSchema,
} from "@covertree/new-business-forms";

interface InsuredTabSchemaParams {
  isTenant: boolean;
  state?: StateCode;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Validation schema for the Insured Info endorsement step.
 */
export const getInsuredTabFormSchema = ({
  isTenant,
  state,
  minDate,
  maxDate,
}: InsuredTabSchemaParams) => {
  const termMessage =
    "Endorsement effective date must be today or later and fall within the policy term";

  let endorsementEffectiveDate = date()
    .typeError("Endorsement effective date is required")
    .required("Endorsement effective date is required")
    .test("not-in-past", termMessage, (value) => value >= startOfToday());

  if (minDate) {
    endorsementEffectiveDate = endorsementEffectiveDate.min(
      minDate,
      termMessage
    );
  }

  if (maxDate) {
    endorsementEffectiveDate = endorsementEffectiveDate.max(
      maxDate,
      termMessage
    );
  }

  return getAgentHouseDetailsFormSchema(isTenant, state)
    .concat(
      personalDetailsFormSchema(false).pick([
        "type",
        "firstName",
        "middleName",
        "lastName",
        "insuredDateOfBirth",
        "orgName",
        "orgType",
        "repRelationToOrg",
        "describeOrgType",
      ])
    )
    .concat(
      object({
        propertyStreetAddress: string()
          .trim()
          .required("Street address is required"),
        propertyUnitNumber: unit(),
        propertyCity: string().trim().required("City is required"),
        propertyState: string().trim().required("State is required"),
        propertyZipCode: string()
          .trim()
          .required("ZIP is required")
          .matches(/^\d{5}(-\d{4})?$/, "ZIP must be a valid 5-digit ZIP code"),
        reason: string()
          .trim()
          .max(80, "Reason / memo must be 80 characters or less")
          .optional(),
        endorsementEffectiveDate,
        homeFixtures: mixed().optional(),
      })
    );
};
